import {
  collection,
  doc,
  addDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { dnsLookupService } from './dnsLookupService';
import { ACTIVE_DOMAIN } from '../config/domainConfig';
import { domainVerificationService } from './domainVerificationService';

export interface SendingDomain {
  id: string;
  userId: string;
  domain: string; // e.g., "send.scubamail.com"
  subdomain: string; // e.g., "user123.gmail.com"
  fullAddress: string; // e.g., "user123.gmail.com@send.scubamail.com"
  status: 'pending' | 'verified' | 'active' | 'failed' | 'warming';
  dnsRecords: DNSRecord[];
  verificationToken: string;
  createdAt: Timestamp;
  verifiedAt?: Timestamp;
  lastCheckedAt?: Timestamp;
  replyToEmail?: string; // Original email for replies
  isDefault?: boolean; // Whether this is the default sending domain
  warmingInfo?: {
    day: number;
    dailyCount: number;
    startedAt: Timestamp;
    lastActiveAt: Timestamp | null;
  };
}

export interface DNSRecord {
  type: 'TXT' | 'CNAME' | 'MX' | 'A';
  name: string;
  value: string;
  status: 'verified' | 'pending' | 'failed';
  verifiedAt?: Timestamp;
  priority?: number; // For MX records
}

export interface EmailAddressMapping {
  originalEmail: string; // e.g., "user@gmail.com"
  sendingAddress: string; // e.g., "user.gmail.com@send.scubamail.com"
  replyToEmail: string; // e.g., "user@gmail.com"
  isActive: boolean;
}

class SendingDomainService {
  private collectionName = 'sendingDomains';
  private mappingCollectionName = 'emailAddressMappings';
  private defaultSendingDomain = ACTIVE_DOMAIN.sendingDomain; // Your main sending domain

  // Generate unique verification token
  private generateVerificationToken(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  // Generate subdomain from email address
  private generateSubdomainFromEmail(email: string): string {
    const [localPart, domain] = email.split('@');
    // Convert dots to hyphens and remove special characters
    const cleanLocalPart = localPart.replace(/[^a-zA-Z0-9]/g, '-');
    // For free email providers, use the format: username.domain
    const freeEmailDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com'];
    if (freeEmailDomains.includes(domain.toLowerCase())) {
      return `${cleanLocalPart}.${domain}`;
    }
    return cleanLocalPart;
  }

  // Generate DNS records for sending domain verification
  private generateDNSRecords(domain: string, verificationToken: string): DNSRecord[] {
    const records: DNSRecord[] = [
      // SPF Record
      {
        type: 'TXT',
        name: domain,
        value: `v=spf1 include:amazonses.com include:${this.defaultSendingDomain} ~all`,
        status: 'pending'
      },
      // DKIM Record
      {
        type: 'CNAME',
        name: `scubamail._domainkey.${domain}`,
        value: `scubamail._domainkey.${this.defaultSendingDomain}`,
        status: 'pending'
      },
      // DMARC Record
      {
        type: 'TXT',
        name: `_dmarc.${domain}`,
        value: `v=DMARC1; p=quarantine; rua=mailto:dmarc@${this.defaultSendingDomain}; ruf=mailto:dmarc@${this.defaultSendingDomain}; fo=1`,
        status: 'pending'
      },
      // Verification Record
      {
        type: 'TXT',
        name: `scubamail-verification.${domain}`,
        value: `scubamail-verification=${verificationToken}`,
        status: 'pending'
      },
      // MX Record for replies
      {
        type: 'MX',
        name: domain,
        value: `10 mail.${this.defaultSendingDomain}`,
        status: 'pending',
        priority: 10
      }
    ];

    return records;
  }

  // Create a new sending domain
  async createSendingDomain(
    userId: string,
    domain: string,
    replyToEmail?: string
  ): Promise<string> {
    try {
      // Check if domain already exists
      const existingDomain = await this.getSendingDomainByDomain(userId, domain);
      if (existingDomain) {
        throw new Error('Sending domain already exists for this user');
      }

      const verificationToken = this.generateVerificationToken();
      const dnsRecords = this.generateDNSRecords(domain, verificationToken);

      const sendingDomainData: Omit<SendingDomain, 'id'> = {
        userId,
        domain: domain.toLowerCase(),
        subdomain: this.generateSubdomainFromEmail(replyToEmail || `${userId}@${domain}`),
        fullAddress: `${this.generateSubdomainFromEmail(replyToEmail || `${userId}@${domain}`)}@${domain}`,
        status: 'pending',
        dnsRecords,
        verificationToken,
        createdAt: Timestamp.now(),
        replyToEmail,
        isDefault: false
      };

      const docRef = await addDoc(collection(db, this.collectionName), sendingDomainData);
      console.log('Created sending domain with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error creating sending domain:', error);
      throw new Error('Failed to create sending domain');
    }
  }

  // Create email address mapping for free email services
  async createEmailAddressMapping(
    userId: string,
    originalEmail: string,
    sendingDomainId: string
  ): Promise<string> {
    try {
      const sendingDomain = await this.getSendingDomain(sendingDomainId);
      if (!sendingDomain) {
        throw new Error('Sending domain not found');
      }

      const subdomain = this.generateSubdomainFromEmail(originalEmail);
      const sendingAddress = `${subdomain}@${sendingDomain.domain}`;

      const mappingData = {
        userId,
        originalEmail: originalEmail.toLowerCase(),
        sendingAddress,
        replyToEmail: originalEmail,
        isActive: true,
        sendingDomainId,
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, this.mappingCollectionName), mappingData);
      console.log('Created email address mapping with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error creating email address mapping:', error);
      throw new Error('Failed to create email address mapping');
    }
  }

  // Get sending domain by ID
  async getSendingDomain(domainId: string): Promise<SendingDomain | null> {
    try {
      const docRef = doc(db, this.collectionName, domainId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as SendingDomain;
      }
      return null;
    } catch (error) {
      console.error('Error getting sending domain:', error);
      throw new Error('Failed to get sending domain');
    }
  }

  // Get sending domain by domain name
  async getSendingDomainByDomain(userId: string, domain: string): Promise<SendingDomain | null> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        where('domain', '==', domain.toLowerCase())
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() } as SendingDomain;
      }
      return null;
    } catch (error) {
      console.error('Error getting sending domain by domain:', error);
      throw new Error('Failed to get sending domain');
    }
  }

  // Get all sending domains for a user
  async getUserSendingDomains(userId: string): Promise<SendingDomain[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      const results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SendingDomain[];

      // Sort by creation date (newest first)
      results.sort((a, b) => {
        const aTime = a.createdAt?.toMillis() || 0;
        const bTime = b.createdAt?.toMillis() || 0;
        return bTime - aTime;
      });

      return results;
    } catch (error) {
      console.error('Error getting user sending domains:', error);
      throw new Error('Failed to get user sending domains');
    }
  }

  // Verify DNS records for sending domain
  async verifySendingDomain(domainId: string): Promise<{
    success: boolean;
    message: string;
    verifiedRecords: string[]
  }> {
    try {
      const sendingDomain = await this.getSendingDomain(domainId);
      if (!sendingDomain) {
        return {
          success: false,
          message: 'Sending domain not found',
          verifiedRecords: []
        };
      }

      // Check if domain exists
      const domainExists = await dnsLookupService.checkDomainExists(sendingDomain.domain);
      if (!domainExists) {
        return {
          success: false,
          message: `Domain ${sendingDomain.domain} does not exist or is not reachable`,
          verifiedRecords: []
        };
      }

      // Verify DNS records
      const verificationResults = await dnsLookupService.verifyRecords(sendingDomain.dnsRecords);

      // Update records based on verification results
      const updatedRecords = sendingDomain.dnsRecords.map(record => {
        const result = verificationResults.find(r => r.recordName === record.name);
        if (result && result.verified) {
          return {
            ...record,
            status: 'verified' as const,
            verifiedAt: Timestamp.now()
          };
        } else if (result && !result.verified) {
          return {
            ...record,
            status: 'failed' as const
          };
        }
        return record;
      });

      // Count verified records
      const verifiedRecords = updatedRecords
        .filter(record => record.status === 'verified')
        .map(record => record.name);

      // Check if all required records are verified
      const allVerified = updatedRecords.every(record => record.status === 'verified');
      const hasFailures = updatedRecords.some(record => record.status === 'failed');

      let newStatus: 'pending' | 'verified' | 'active' | 'failed';
      if (allVerified) {
        newStatus = 'verified';
      } else if (hasFailures) {
        newStatus = 'failed';
      } else {
        newStatus = 'pending';
      }

      const verifiedAt = allVerified ? Timestamp.now() : undefined;

      // Update the sending domain
      await updateDoc(doc(db, this.collectionName, domainId), {
        dnsRecords: updatedRecords,
        status: newStatus,
        verifiedAt,
        lastCheckedAt: Timestamp.now()
      });

      // Generate appropriate message
      let message: string;
      if (allVerified) {
        message = 'Sending domain verification completed successfully! All DNS records are properly configured.';
      } else if (hasFailures) {
        const failedRecords = verificationResults.filter(r => !r.verified);
        message = `Verification failed. ${failedRecords.length} DNS record(s) are missing or incorrect.`;
      } else {
        message = `Some DNS records are still pending. Verified: ${verifiedRecords.length}/${updatedRecords.length}. Please wait for DNS propagation and try again.`;
      }

      return {
        success: allVerified,
        message,
        verifiedRecords
      };
    } catch (error) {
      console.error('Error verifying sending domain:', error);
      return {
        success: false,
        message: `Failed to verify sending domain: ${error instanceof Error ? error.message : 'Unknown error'}`,
        verifiedRecords: []
      };
    }
  }

  // Get sending address for an email
  // If the email's domain is verified, return the original email for direct sending
  // Otherwise, generate a subdomain-based sending address
  async getSendingAddressForEmail(userId: string, email: string, workspaceId?: string): Promise<string> {
    try {
      // FIRST: Check if the email's domain is verified (allows direct business email sending)
      const domainCheck = await domainVerificationService.isEmailDomainVerified(userId, email, workspaceId);
      if (domainCheck.isVerified) {
        console.log(`📧 Using verified domain email directly: ${email}`);
        return email; // Return original email for direct sending from verified domain
      }

      // Check if there's a custom mapping
      const q = query(
        collection(db, this.mappingCollectionName),
        where('userId', '==', userId),
        where('originalEmail', '==', email.toLowerCase()),
        where('isActive', '==', true)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const mapping = querySnapshot.docs[0].data() as EmailAddressMapping;
        return mapping.sendingAddress;
      }

      // If no custom mapping, check if user has verified sending domains
      const sendingDomains = await this.getUserSendingDomains(userId);
      const activeDomain = sendingDomains.find(domain => domain.status === 'verified' || domain.status === 'active');

      if (activeDomain) {
        const subdomain = this.generateSubdomainFromEmail(email);
        return `${subdomain}@${activeDomain.domain}`;
      }

      // Fallback to default sending domain (subdomain format for free emails)
      const subdomain = this.generateSubdomainFromEmail(email);
      return `${subdomain}@${this.defaultSendingDomain}`;
    } catch (error) {
      console.error('Error getting sending address for email:', error);
      // Fallback to default
      const subdomain = this.generateSubdomainFromEmail(email);
      return `${subdomain}@${this.defaultSendingDomain}`;
    }
  }

  // Get reply-to email for a sending address
  async getReplyToEmail(sendingAddress: string): Promise<string | null> {
    try {
      const q = query(
        collection(db, this.mappingCollectionName),
        where('sendingAddress', '==', sendingAddress),
        where('isActive', '==', true)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const mapping = querySnapshot.docs[0].data() as EmailAddressMapping;
        return mapping.replyToEmail;
      }

      return null;
    } catch (error) {
      console.error('Error getting reply-to email:', error);
      return null;
    }
  }

  // Get DNS instructions for sending domain
  getDNSInstructions(domain: string, verificationToken: string): {
    records: DNSRecord[];
    instructions: string[];
  } {
    const records = this.generateDNSRecords(domain, verificationToken);

    const instructions = [
      'Log in to your domain registrar or DNS provider',
      'Navigate to DNS management or DNS settings',
      'Add the following DNS records exactly as shown',
      'Wait 5-10 minutes for DNS propagation',
      'Click "Verify Domain" to check your records'
    ];

    return { records, instructions };
  }

  // Delete sending domain
  async deleteSendingDomain(domainId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.collectionName, domainId));
    } catch (error) {
      console.error('Error deleting sending domain:', error);
      throw new Error('Failed to delete sending domain');
    }
  }

  // Set default sending domain
  async setDefaultSendingDomain(userId: string, domainId: string): Promise<void> {
    try {
      // First, unset all other domains as default
      const userDomains = await this.getUserSendingDomains(userId);
      const updatePromises = userDomains.map(domain =>
        updateDoc(doc(db, this.collectionName, domain.id), { isDefault: false })
      );
      await Promise.all(updatePromises);

      // Set the selected domain as default
      await updateDoc(doc(db, this.collectionName, domainId), { isDefault: true });
    } catch (error) {
      console.error('Error setting default sending domain:', error);
      throw new Error('Failed to set default sending domain');
    }
  }
}

export const sendingDomainService = new SendingDomainService();
